import axios, { AxiosError } from 'axios';

import createUrlWithParams from 'services/common/createUrlWithParams/createUrlWithParams';

import type { DefaultApiResponse, PaginatedApiResponse, PaginatedData } from '../_types';
import type { ExerciseQueueUuid } from './exerciseQueues.branded';
import {
  parseExerciseQueueAnswerResponseForUI,
  parseExerciseQueueAvailabilityForUI,
  parseExerciseQueueAvailabilityListForUI,
  parseExerciseQueueCreateFormInputForAPI,
  parseExerciseQueueExplainResponseForUI,
  parseExerciseQueueForUI,
  parsePaginatedExerciseQueueListForUI,
} from './exerciseQueues.parsers';
import type {
  ExerciseQueue,
  ExerciseQueueAnswerRequestDto,
  ExerciseQueueAnswerResponse,
  ExerciseQueueAnswerResponseDto,
  ExerciseQueueCreateFormInput,
  ExerciseQueueDto,
  ExerciseQueueExplainFeedbackRequestDto,
  ExerciseQueueExplainRequestDto,
  ExerciseQueueAvailability,
  ExerciseQueueAvailabilityDto,
  ExerciseQueueExplainResponse,
  ExerciseQueueExplainResponseDto,
  ExerciseQueueList,
  ExerciseQueueListDto,
  ExerciseQueueRetrieveQueryDto,
  ExerciseQueueSetCurrentRequestDto,
  ExerciseQueuesListQueryDto,
} from './exerciseQueues.types';
import { SubjectPk } from 'types/Subject';

/** Paths match OpenAPI: `/api/v1/exercises/exercise_queues/` (base URL should include `/api/v1/`). */
const basePath = 'exercises/exercise_queues/';

type GetActiveQuery = () => Promise<ExerciseQueue | null>;
type GetListQuery = () => Promise<PaginatedData<ExerciseQueueList>>;
type GetByUuidQuery = () => Promise<ExerciseQueue>;
type CreateMutation = (data: ExerciseQueueCreateFormInput) => Promise<ExerciseQueue>;
type AnswerMutation = (data: ExerciseQueueAnswerRequestDto) => Promise<ExerciseQueueAnswerResponse>;
type SkipMutation = () => Promise<ExerciseQueue>;
type SetCurrentMutation = (data: ExerciseQueueSetCurrentRequestDto) => Promise<ExerciseQueue>;
type CloseMutation = () => Promise<ExerciseQueue>;
type ExplainQuery = (data: ExerciseQueueExplainRequestDto) => Promise<ExerciseQueueExplainResponse>;
type ExplainFeedbackMutation = (data: ExerciseQueueExplainFeedbackRequestDto) => Promise<void>;
type GetAvailabilityQuery = () => Promise<ExerciseQueueAvailability>;
type GetAvailabilityListQuery = () => Promise<ExerciseQueueAvailability[]>;

const exerciseQueuesApi = {
  getActive:
    (subjectPk: SubjectPk): GetActiveQuery =>
    async () => {
      try {
        const response = await axios.get<GetActiveQuery, DefaultApiResponse<ExerciseQueueDto>>(
          createUrlWithParams(`${basePath}active/`, { subject: subjectPk }),
        );
        return parseExerciseQueueForUI(response.data);
      } catch (error) {
        if (axios.isAxiosError(error)) {
          const axiosError = error as AxiosError;
          if (axiosError.response?.status === 404) {
            return null;
          }
        }
        throw error;
      }
    },

  getList:
    (params?: ExerciseQueuesListQueryDto): GetListQuery =>
    async () => {
      const response = await axios.get<GetListQuery, PaginatedApiResponse<ExerciseQueueListDto>>(createUrlWithParams(basePath, params));
      return parsePaginatedExerciseQueueListForUI(response.data);
    },

  getByUuid:
    (queueUuid: ExerciseQueueUuid, query?: ExerciseQueueRetrieveQueryDto): GetByUuidQuery =>
    async () => {
      const response = await axios.get<GetByUuidQuery, DefaultApiResponse<ExerciseQueueDto>>(
        createUrlWithParams(`${basePath}${queueUuid}/`, query),
      );
      return parseExerciseQueueForUI(response.data);
    },

  create: (): CreateMutation => async data => {
    const response = await axios.post<CreateMutation, DefaultApiResponse<ExerciseQueueDto>>(
      basePath,
      parseExerciseQueueCreateFormInputForAPI(data),
    );
    return parseExerciseQueueForUI(response.data);
  },

  answer:
    (queueUuid: ExerciseQueueUuid): AnswerMutation =>
    async data => {
      const response = await axios.post<AnswerMutation, DefaultApiResponse<ExerciseQueueAnswerResponseDto>>(
        `${basePath}${queueUuid}/answer/`,
        data,
      );
      return parseExerciseQueueAnswerResponseForUI(response.data);
    },

  skip:
    (queueUuid: ExerciseQueueUuid): SkipMutation =>
    async () => {
      const response = await axios.post<SkipMutation, DefaultApiResponse<ExerciseQueueDto>>(`${basePath}${queueUuid}/skip/`);
      return parseExerciseQueueForUI(response.data);
    },

  setCurrent:
    (queueUuid: ExerciseQueueUuid): SetCurrentMutation =>
    async data => {
      const response = await axios.post<SetCurrentMutation, DefaultApiResponse<ExerciseQueueDto>>(
        `${basePath}${queueUuid}/set_current/`,
        data,
      );
      return parseExerciseQueueForUI(response.data);
    },

  close:
    (queueUuid: ExerciseQueueUuid): CloseMutation =>
    async () => {
      const response = await axios.post<CloseMutation, DefaultApiResponse<ExerciseQueueDto>>(`${basePath}${queueUuid}/close/`);
      return parseExerciseQueueForUI(response.data);
    },

  explain: (): ExplainQuery => async data => {
    const response = await axios.post<ExplainQuery, DefaultApiResponse<ExerciseQueueExplainResponseDto>>(`${basePath}explain/`, data);
    return parseExerciseQueueExplainResponseForUI(response.data);
  },

  explainFeedback: (): ExplainFeedbackMutation => async data => {
    await axios.post(`${basePath}explain_feedback/`, data);
  },

  getAvailability:
    (subjectPk: SubjectPk): GetAvailabilityQuery =>
    async () => {
      const response = await axios.get<GetAvailabilityQuery, DefaultApiResponse<ExerciseQueueAvailabilityDto>>(
        createUrlWithParams(`${basePath}availability/`, { subject: subjectPk }),
      );
      return parseExerciseQueueAvailabilityForUI(response.data);
    },

  getAvailabilityList: (): GetAvailabilityListQuery => async () => {
    const response = await axios.get<GetAvailabilityListQuery, DefaultApiResponse<ExerciseQueueAvailabilityDto[]>>(
      `${basePath}availability/`,
    );
    return parseExerciseQueueAvailabilityListForUI(response.data);
  },
};

export const exerciseQueuesApiKeys = {
  active: (subjectPk: SubjectPk) => ['exerciseQueues', 'active', subjectPk] as const,
  availability: (subjectPk: SubjectPk) => ['exerciseQueues', 'availability', subjectPk] as const,
  availabilityList: () => ['exerciseQueues', 'availability', 'list'] as const,
  list: (params?: ExerciseQueuesListQueryDto) => ['exerciseQueues', 'list', params ?? {}] as const,
  detail: (queueUuid: ExerciseQueueUuid, query?: ExerciseQueueRetrieveQueryDto) =>
    ['exerciseQueues', 'detail', queueUuid, query ?? {}] as const,
  explain: (params: ExerciseQueueExplainRequestDto) => ['exerciseQueues', 'explain', params] as const,
};

const SIX_HOURS_MS = 6 * 60 * 60 * 1000;

export const exerciseQueuesAvailabilityQueryOptions = {
  staleTime: SIX_HOURS_MS,
  refetchOnWindowFocus: false,
  refetchOnMount: false,
  refetchOnReconnect: false,
};

export default exerciseQueuesApi;
